import { Table, TableBody, TableCell, TableContainer, TableRow, Typography, Box, Skeleton, IconButton, Button } from '@mui/material';
import { Settings, AccessTimeFilled, CheckCircle } from '@mui/icons-material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { getAllAutojobs } from 'src/shared/services/autojobService';
import { formatDateAndTime } from 'src/shared/functions/formatDate';
import { HeaderSubTitle } from '../atoms/headerSubTitle';
import { AutojobResponse } from '../../models/autojobResponse';
import { UpcomingAutojobModal } from './upcomingJobsModal';
import { AllUpcomingAutojobsModal } from './allUpcomingAutojobsModal';
import { ChartFailMessage } from '../atoms/chartFailMessage';

export const AutojobsChart = () => {
  const { t } = useTranslation(['dashboard']);

  const isMounted = useIsMounted();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [hasError, setHasError] = useState<boolean>(false);
  const [openUpcomingJobModal, setOpenUpcomingJobModal] = useState<boolean>(false);
  const [openAllAutojobsModal, setOpenAllAutojobsModal] = useState<boolean>(false);
  const [allAutojobs, setAllAutojobs] = useState<AutojobResponse[]>([]);
  const [selectedAutojob, setSelectedAutojob] = useState<AutojobResponse | undefined>(undefined);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const result = await getAllAutojobs({ pageSize: 5, status: 1 });
        if (isMounted()) {
          setAllAutojobs(result.data.results);
        }
      } catch (e) {
        if (isMounted()) {
          ConsoleHelper.error(e);
          setHasError(true);
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [isMounted, t]);

  const extractFirstNumber = (statusMsg: string): number | '-' => {
    const match = statusMsg?.match(/\d+/); // hittar första förekomsten av en eller flera siffror
    return match ? parseInt(match[0], 10) : '-';
  };

  const TextItem = ({ text }) => (
    <Typography variant='body2' color={(thm) => thm.palette.text.primary}>
      {text}
    </Typography>
  );

  return (
    <Box sx={{ p: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <HeaderSubTitle label={t('dashboard:autojobs.chartHeader')} />
      </Box>
      {isLoading && <Skeleton variant='rectangular' animation='wave' sx={{ height: '500px', borderRadius: 1 }} />}
      {hasError && <ChartFailMessage />}
      {allAutojobs && !isLoading && !hasError && (
        <Box mt={1}>
          <Typography variant='subtitle1'>{t('dashboard:autojobs.chart.latest')}</Typography>
          {allAutojobs && allAutojobs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableBody>
                  {allAutojobs.map((item) => {
                    return (
                      <TableRow key={item.task_id}>
                        <TableCell>
                          <CheckCircle fontSize='small' color='success' />
                        </TableCell>
                        <TableCell>
                          <TextItem text={item.taskName} />
                        </TableCell>
                        <TableCell>
                          <TextItem text={`${extractFirstNumber(item.lastStatusMsg)} ${t('dashboard:piciesShort')}`} />
                        </TableCell>
                        <TableCell align='right'>
                          <TextItem text={formatDateAndTime(item.lastExecuted ?? undefined)} />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: 'grey.500', py: 1 }}>{t('dashboard:autojobs.chart.noLatestJobs')}</Typography>
          )}
        </Box>
      )}
      {allAutojobs && !isLoading && !hasError && (
        <Box mt={1.5}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography>{t('dashboard:autojobs.chart.upcoming')}</Typography>
            <Button variant='text' onClick={() => setOpenAllAutojobsModal(true)} sx={{ textTransform: 'none', textDecoration: 'underline' }}>
              {t('dashboard:autojobs.chartLink')}
            </Button>
          </Box>
          {allAutojobs && allAutojobs.length > 0 ? (
            <TableContainer>
              <Table>
                <TableBody>
                  {allAutojobs.map((item) => {
                    return (
                      <TableRow key={item.task_id}>
                        <TableCell>
                          <AccessTimeFilled fontSize='small' color='disabled' />
                        </TableCell>
                        <TableCell>
                          <TextItem text={formatDateAndTime(item.startTimeStamp)} />
                        </TableCell>
                        <TableCell>
                          <TextItem text={item.taskName} />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size='small'
                            onClick={() => {
                              setSelectedAutojob(item);
                              setOpenUpcomingJobModal(true);
                            }}
                          >
                            <Settings fontSize='small' color='primary' />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Typography sx={{ color: 'grey.500', py: 1 }}>{t('dashboard:autojobs.chart.noUpcomingJobs')}</Typography>
          )}
        </Box>
      )}
      {selectedAutojob && (
        <UpcomingAutojobModal
          modalOpen={openUpcomingJobModal}
          onClose={() => {
            setSelectedAutojob(undefined);
            setOpenUpcomingJobModal(false);
          }}
          taskId={selectedAutojob.task_id}
        />
      )}
      {openAllAutojobsModal && (
        <AllUpcomingAutojobsModal
          modalOpen={openAllAutojobsModal}
          onClose={() => {
            setOpenAllAutojobsModal(false);
          }}
        />
      )}
    </Box>
  );
};
